"use client";

import { useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import "katex/dist/katex.min.css";
import { getColorPreset } from "@/lib/themes";
import type { Components } from "react-markdown";

// Mermaid rendering component
function MermaidBlock({ chart }: { chart: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);

  const renderChart = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      const mermaid = (await import("mermaid")).default;
      mermaid.initialize({
        startOnLoad: false,
        theme: "default",
        securityLevel: "loose",
      });
      const { svg } = await mermaid.render(idRef.current, chart);
      if (containerRef.current) {
        containerRef.current.innerHTML = svg;
      }
    } catch {
      if (containerRef.current) {
        containerRef.current.innerHTML = `<pre style="color:#F87171;font-size:0.8rem;">Invalid mermaid syntax</pre>`;
      }
    }
  }, [chart]);

  useEffect(() => {
    renderChart();
  }, [renderChart]);

  return (
    <div
      ref={containerRef}
      style={{
        margin: "1.5rem 0",
        display: "flex",
        justifyContent: "center",
        overflow: "auto",
      }}
    />
  );
}

// Theme color maps for the preview pane
const themeStyles: Record<string, React.CSSProperties> = {
  paper: {
    backgroundColor: "#FFFFFF",
    color: "#1A1A1A",
  },
  clean: {
    backgroundColor: "#0C0E12",
    color: "#EDEEF0",
  },
  dark: {
    backgroundColor: "#08090C",
    color: "#A8AAB0",
  },
};

const themeVars: Record<string, Record<string, string>> = {
  paper: {
    "--preview-heading": "#111111",
    "--preview-code-bg": "#F5F5F5",
    "--preview-pre-bg": "#F5F5F5",
    "--preview-pre-border": "#E5E5E5",
    "--preview-border": "#E5E5E5",
    "--preview-muted": "#666666",
    "--preview-pre-code": "#1A1A1A",
  },
  clean: {
    "--preview-heading": "#EDEEF0",
    "--preview-code-bg": "#1A1D25",
    "--preview-pre-bg": "#13161C",
    "--preview-pre-border": "rgba(255,255,255,0.06)",
    "--preview-border": "rgba(255,255,255,0.06)",
    "--preview-muted": "#8B8F9A",
    "--preview-pre-code": "#EDEEF0",
  },
  dark: {
    "--preview-heading": "#C8CACF",
    "--preview-code-bg": "#121418",
    "--preview-pre-bg": "#0E1014",
    "--preview-pre-border": "rgba(255,255,255,0.04)",
    "--preview-border": "rgba(255,255,255,0.04)",
    "--preview-muted": "#6B6E78",
    "--preview-pre-code": "#A8AAB0",
  },
};

interface PreviewProps {
  content: string;
  className?: string;
  theme?: string;
  colorPreset?: string;
}

export default function Preview({ content, className, theme = "paper", colorPreset }: PreviewProps) {
  const preset = colorPreset ? getColorPreset(colorPreset) : null;
  const baseStyle = themeStyles[theme] || themeStyles.paper;
  const vars = themeVars[theme] || themeVars.paper;
  const isDark = theme === "clean" || theme === "dark";

  const components: Components = {
    code({ className: codeClassName, children, ...props }) {
      const match = /language-(\w+)/.exec(codeClassName || "");
      const lang = match ? match[1] : "";

      if (lang === "mermaid") {
        return <MermaidBlock chart={String(children).trim()} />;
      }

      // For inline code (no language class), render as inline
      if (!match) {
        return <code className={codeClassName} {...props}>{children}</code>;
      }

      // Block code — let rehype-highlight handle it
      return <code className={codeClassName} {...props}>{children}</code>;
    },
    // Prevent pre wrapper from wrapping mermaid blocks
    pre({ children, ...props }) {
      // Check if the child is a mermaid block (MermaidBlock renders a div, not code)
      const child = children as React.ReactElement;
      if (child?.type === MermaidBlock) {
        return <>{children}</>;
      }
      return <pre {...props}>{children}</pre>;
    },
  };

  return (
    <div
      className={`preview-prose ${className || ""}`}
      style={{
        padding: "32px",
        overflowY: "auto",
        borderLeft: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #E8E8E8",
        minHeight: "auto",
        ...baseStyle,
        ...Object.fromEntries(Object.entries(vars)),
        ["--link" as string]: preset?.accent || (isDark ? "#E8915A" : "#8B4513"),
        ["--link-hover" as string]: preset?.accentHover || (isDark ? "#D47A45" : "#6B3410"),
        ["--blockquote-border" as string]: preset?.accent || (isDark ? "#E8915A" : "#8B4513"),
        ["--code-text" as string]: preset?.accent || (isDark ? "#E8915A" : "#8B4513"),
      } as React.CSSProperties}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeHighlight, rehypeSlug, [rehypeAutolinkHeadings, { behavior: "wrap" }]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
